/**
 * Field types that can be used in the Form Schema
 * @see FormJSONObject
 */
export const IkeFieldType = {
	DATE: 'date' as const,
	FORM: 'form' as const,
	LOCATION: 'location' as const,
	NESTED_LIST: 'nestedlist' as const,
	NUMBER: 'number' as const,
	ORIENTATION: 'orientation' as const,
	SELECT_LIST: 'selectlist' as const,
	TEXT: 'text' as const,
	HEIGHT: 'height' as const,
	TRUE_SIZE: 'truesizecapture' as const,
	YES_NO: 'yesno' as const,
	/**
	 * Generic image with no sensor data
	 * @see IkeCaptureType.IMAGE
	 */
	IMAGE: 'image' as const,
	VECTOR: 'vector' as const,
	VERTICAL_SEPARATION: 'verticalseparation' as const,
	FILE_TOOL: 'filetool' as const,
	ANCHOR: 'anchor' as const,
	LINE: 'line' as const,
	/**
	 * Dedicated MidSpan capture: an IKE photo of the span paired with a ground reference shot, from which
	 * a locked base marker is derived. Behaves as a TRUE_SIZE photo everywhere else.
	 * @see IkeToken.FEATURE_MIDSPAN_PHOTO_TOOL
	 */
	MID_SPAN: 'midspan' as const,
	ASSET_TYPE: 'assettype' as const,
};
export type IkeFieldType =
	| typeof IkeFieldType.DATE
	| typeof IkeFieldType.FORM
	| typeof IkeFieldType.LOCATION
	| typeof IkeFieldType.NESTED_LIST
	| typeof IkeFieldType.NUMBER
	| typeof IkeFieldType.ORIENTATION
	| typeof IkeFieldType.SELECT_LIST
	| typeof IkeFieldType.TEXT
	| typeof IkeFieldType.HEIGHT
	| typeof IkeFieldType.TRUE_SIZE
	| typeof IkeFieldType.YES_NO
	| typeof IkeFieldType.IMAGE
	| typeof IkeFieldType.VECTOR
	| typeof IkeFieldType.VERTICAL_SEPARATION
	| typeof IkeFieldType.FILE_TOOL
	| typeof IkeFieldType.ANCHOR
	| typeof IkeFieldType.LINE
	| typeof IkeFieldType.MID_SPAN
	| typeof IkeFieldType.ASSET_TYPE;

/**
 * Any measurable IKE photo field type — a standard IKE Photo or a MidSpan capture. Use this wherever a
 * schema `type` is being tested, so a MidSpan field is not mistaken for a non-photo field. The collection
 * model equivalent is `CollectionModelFieldTrueSize.isMeasurableIkePhotoField`.
 */
export function isMeasurableIkePhotoFieldType(type: IkeFieldType): boolean {
	return type === IkeFieldType.TRUE_SIZE || type === IkeFieldType.MID_SPAN;
}

/**
 * How a MID_SPAN field is represented on the public v1 API: as a standard IKE Photo, with `subtype`
 * carrying the dedicated-tool fact. `midspan` is not a legal `type` in the v1 schemas, so IKE Field builds
 * that predate the tool render the field as an ordinary photo instead of dropping it.
 *
 * Applies to the field wherever it sits — a Span subform, or loose at the top of a customer's form.
 *
 * Every place that masks or unmasks a field for the public API goes through this pair; the form serializer
 * and the collection field serializer encoded it separately, and a divergence between them fails silently.
 */
export function maskMidSpanType(type: IkeFieldType): {
	type: IkeFieldType;
	subtype?: typeof IkeFieldType.MID_SPAN;
} {
	return type === IkeFieldType.MID_SPAN ? { type: IkeFieldType.TRUE_SIZE, subtype: IkeFieldType.MID_SPAN } : { type };
}

export function isMaskedMidSpanType(json: { type: IkeFieldType; subtype?: IkeFieldType }): boolean {
	return json.type === IkeFieldType.TRUE_SIZE && json.subtype === IkeFieldType.MID_SPAN;
}
